import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class ApiRestInterceptor implements HttpInterceptor {

  constructor( private cognito: CognitoService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.headers.get("skip"))
    {
      let request_without_skip = request.clone({ headers: request.headers.delete('skip','true') });
      return next.handle(request_without_skip);
    }
    if(request.url.includes(environment.s3.bucket)){
      return next.handle(request);
    }
    if(request.url.includes('/denounces/public/')){
      return next.handle(request);
    }

    return from(this.resolverJwt())
        .pipe(
            switchMap((jwt: string | null) => {
                if (!jwt) {
                  return throwError(() => new HttpErrorResponse({
                    status: 401,
                    statusText: 'No autenticado',
                    url: request.url,
                  }));
                }
                let with_auth_request = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${jwt}`
                    }
                });
                return next.handle(with_auth_request);
            }),
            catchError((err) => {
                if (err instanceof HttpErrorResponse) {
                  return throwError(() => err);
                }
                return throwError(() => new HttpErrorResponse({
                  status: 401,
                  statusText: 'Sesión no disponible',
                  url: request.url,
                }));
            })
        );

}

  /**
   * El token con el que se firma la petición, o `null` si no hay sesión.
   *
   * Pregunta dos veces antes de rendirse. Un `null` de `getSession()` no
   * siempre significa "no hay sesión": también sale cuando
   * `Auth.currentSession()` tardó más que su carrera de 10 s o cuando un
   * refresh puntual falló. Ese null se convierte acá en un 401 y la petición
   * se pierde, aunque la sesión estuviera perfectamente bien — y le toca
   * justo a la primera petición después de una pausa, que es cuando el caché
   * de 30 s ya venció: el clic deliberado del usuario.
   *
   * El fallo no se cachea (`_fetchSession` lo deja explícito), así que la
   * segunda pregunta vuelve a intentar de verdad. No se limpia el caché a
   * mano: eso borraría también el del usuario, que usan los guards, y una
   * ráfaga de fallos se traduciría en una ráfaga de llamadas a Cognito.
   */
  private async resolverJwt(): Promise<string | null> {
    const primera = await this.cognito.getSession();
    const jwt = primera?.idToken?.jwtToken;
    if (jwt) return jwt;

    // Durante el corte por tope de 12 h el null es a propósito: insistir sólo
    // demoraría la salida.
    if (this.cognito.isForcingLogout()) return null;

    const segunda = await this.cognito.getSession();
    return segunda?.idToken?.jwtToken ?? null;
  }
}
